import { FunctionComponent, useState } from 'react';
import { useRouter } from 'next/router';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import classnames from 'classnames';

import { Locales } from 'types/locale';
import { Routes } from 'types/routes';

import { Types } from 'models/UserAgree';

import { select as selectLocale } from 'utils/locale';

import Button, { ButtonLayouts, ButtonTypes } from 'components/layout/Button';
import Checkbox from 'components/layout/forms/Checkbox';
import Form from 'components/layout/forms/Form';
import Input from 'components/layout/forms/Input';
import InputRow from 'components/layout/forms/InputRow';

import { ErrorMessages, Props } from './index';
import StyledComponent from './styles';

interface StateTypes {
    isLoaded: boolean;
    data: {
        given_name: string;
        family_name: string;
        email: string;
        sub: string;
    };
}

const LayoutFacebookRegister: FunctionComponent<Props> = ({ actions, buttonProps, googleLabel, registerType }) => {
    const [errorMessage, setErrorMessage]: [ErrorMessages, Function] = useState(null);
    const [state, setState]: [StateTypes, Function] = useState(null);

    const router = useRouter();

    const { isLoaded, data } = state || {};

    const translations = selectLocale({
        [Locales.Pl]: require('./locales/pl.json'),
    });


    const googleLogin = useGoogleLogin({
        onSuccess: async ({ access_token }) => {
            const userInfo = await axios.get(
                'https://www.googleapis.com/oauth2/v3/userinfo',
                { headers: { Authorization: `Bearer ${access_token}` } },
            );
            setState({
                isLoaded: true,
                data: userInfo?.data,
            });
        },
        onError: errorResponse => console.log(errorResponse),
    });
    return (
        <StyledComponent className={classnames('layout-google-register', {
            'visible': Boolean(isLoaded),
        })}
        >
            <Button
                onClick={()=>googleLogin()}
                layout={ButtonLayouts.Wide}
                {...buttonProps}
            >
                {googleLabel}
            </Button>
            {isLoaded && (
                <Form
                    initialValues={{
                        firstName: data?.given_name,
                        lastName: data?.family_name,
                        email: data?.email,
                        googleId: data?.sub,
                    }}
                    submitAction={async (values) => {
                        const getPhoneAgrees = () => {
                            if (values?.phone && values?.phone.length > 0) {
                                return [{
                                    type: Types.PhoneMarketing,
                                    content: translations?.inputs?.phoneMarketing,
                                    value: values?.phoneMarketing || false,
                                }, {
                                    type: Types.PhoneMarketingClients,
                                    content: translations?.inputs?.phoneMarketingClients,
                                    value: values?.phoneMarketingClients || false,
                                }];
                            } else return [];
                        };

                        try {
                            const response = await actions.register({
                                ...values,
                                registerType,
                                userAgrees: [{
                                    type: Types.Regulations,
                                    content: translations?.inputs?.regulations,
                                    value: values?.regulations || false,
                                }, {
                                    type: Types.EmailMarketing,
                                    content: translations?.inputs?.emailMarketing,
                                    value: values?.emailMarketing || false,
                                }, {
                                    type: Types.EmailMarketingClients,
                                    content: translations?.inputs?.emailMarketingClients,
                                    value: values?.emailMarketingClients || false,
                                },
                                ...getPhoneAgrees(),
                                ],
                            });

                            return response;
                        } catch (error) {
                            console.error(error);
                            setErrorMessage(error?.payload?.originalError?.data?.errors);
                            throw error;
                        }
                    }}
                    onSuccess={() => router.push(Routes.PublicActivationAccount)}
                >
                    {({ formik }) => {
                        return (
                            <div className="modal-wrapper">
                                <div className="modal">
                                    <h2 className="modal-title">{translations?.title?.replace('{{name}}', data?.given_name)}</h2>
                                    <p className="modal-subtitle">{translations?.subtitle}</p>
                                    <InputRow className="rows">
                                        <Input
                                            name="firstName"
                                            label={translations?.inputs?.firstName?.label}
                                            placeholder={translations?.inputs?.firstName?.placeholder}
                                            onChange={formik.handleChange}
                                            value={formik.values.firstName}
                                            error={errorMessage?.firstName}
                                        />
                                        <Input
                                            name="lastName"
                                            label={translations?.inputs?.lastName?.label}
                                            placeholder={translations?.inputs?.lastName?.placeholder}
                                            onChange={formik.handleChange}
                                            value={formik.values.lastName}
                                            error={errorMessage?.lastName}
                                        />
                                    </InputRow>
                                    <InputRow className="rows">
                                        <Input
                                            name="email"
                                            label={translations?.inputs?.email?.label}
                                            placeholder={translations?.inputs?.email?.placeholder}
                                            onChange={formik.handleChange}
                                            value={formik.values.email}
                                            error={errorMessage?.email}
                                        />
                                        <Input
                                            name="phone"
                                            label={translations?.inputs?.phone?.label}
                                            placeholder={translations?.inputs?.phone?.placeholder}
                                            onChange={formik.handleChange}
                                            value={formik.values.phone}
                                            error={errorMessage?.phone}
                                        />
                                    </InputRow>
                                    <InputRow className="rows">
                                        <Checkbox
                                            name="regulations"
                                            label={translations?.inputs?.regulations}
                                            onChange={formik.handleChange}
                                            value={formik.values.regulations}
                                            error={errorMessage?.userAgrees}
                                            expandable
                                        />
                                    </InputRow>
                                    {formik?.values?.phone && (
                                        <>
                                            <InputRow>
                                                <Checkbox
                                                    name="phoneMarketing"
                                                    label={translations?.inputs?.phoneMarketing}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.phoneMarketing}
                                                    expandable
                                                />
                                            </InputRow>
                                            <InputRow>
                                                <Checkbox
                                                    name="phoneMarketingClients"
                                                    label={translations?.inputs?.phoneMarketingClients}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.phoneMarketingClients}
                                                    expandable
                                                />
                                            </InputRow>
                                        </>
                                    )}
                                    {formik?.values?.email && (
                                        <>
                                            <InputRow>
                                                <Checkbox
                                                    name="emailMarketing"
                                                    label={translations?.inputs?.emailMarketing}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.emailMarketing}
                                                    expandable
                                                />
                                            </InputRow>
                                            <InputRow>
                                                <Checkbox
                                                    name="emailMarketingClients"
                                                    label={translations?.inputs?.emailMarketingClients}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.emailMarketingClients}
                                                    expandable
                                                />
                                            </InputRow>
                                        </>
                                    )}
                                    <Button
                                        {...translations?.buttonProps}
                                        loading={formik.isSubmitting}
                                        type={ButtonTypes.Submit}
                                    />
                                </div>
                            </div>
                        );
                    }}
                </Form>
            )}

        </StyledComponent>
    );
};

export default LayoutFacebookRegister;